<script setup lang="ts">
import { computed, ref, toValue, watchEffect, type MaybeRefOrGetter } from 'vue'

import type { TeamSide } from '@collector/shared-domain'
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from '@collector/shared-ui'
import type { Participant, Stat } from '@collector/sportsapi-types'

import type { SportsStatConfig } from '../../../interfaces/SportStatConfig'
import { getStatById, isStatSame } from '../../../utils/stats.utils'
import TeamStatisticsItem from './TeamStatisticsItem.vue'

type StatItemData = {
  config: SportsStatConfig
  homeStat: Stat
  homeDirty: boolean
  awayStat: Stat
  awayDirty: boolean
}

type Props = {
  teamStats: SportsStatConfig[]
  teamStatsAccordion: SportsStatConfig[]
  initialHomeParticipant: Participant
  initialAwayParticipant: Participant
}
const props = defineProps<Props>()

// state
const accordion = ref()

const homeTeamDirty = defineModel<boolean>('homeTeamDirty', { default: false })
const awayTeamDirty = defineModel<boolean>('awayTeamDirty', { default: false })
const homeParticipant = defineModel<Participant>('homeParticipant', {
  required: true,
})
const awayParticipant = defineModel<Participant>('awayParticipant', {
  required: true,
})

const statItems = computed(() => getStatItemData(() => props.teamStats))
const statAccordionItems = computed(() =>
  getStatItemData(() => props.teamStatsAccordion),
)
const dirtyState = computed(() => {
  const allItems = [...statItems.value, ...statAccordionItems.value]

  const homeTeamDirtyFlag = allItems.some((item) => item.homeDirty)
  const awayTeamDirtyFlag = allItems.some((item) => item.awayDirty)

  return {
    homeTeamDirty: homeTeamDirtyFlag,
    awayTeamDirty: awayTeamDirtyFlag,
  }
})

// methods
function getStatItemData(
  source: MaybeRefOrGetter<SportsStatConfig[]>,
): StatItemData[] {
  return toValue(source).map((config) => {
    const statId = config.sportStat.id
    const homeStat = getStatById(statId, homeParticipant.value)
    const initialHomeStat = getStatById(statId, props.initialHomeParticipant)
    const awayStat = getStatById(statId, awayParticipant.value)
    const initialAwayStat = getStatById(statId, props.initialAwayParticipant)
    return {
      config,
      homeStat,
      homeDirty: !isStatSame(homeStat, initialHomeStat, config.sportStat),
      awayStat,
      awayDirty: !isStatSame(awayStat, initialAwayStat, config.sportStat),
    }
  })
}

function updateStat(stat: Stat, teamSide: TeamSide): void {
  const participant = teamSide === 'home' ? homeParticipant : awayParticipant
  const statIndex = participant.value.stats.findIndex((s) => s.id === stat.id)

  const updatedStats = [...participant.value.stats]
  updatedStats.splice(statIndex, 1, stat)

  const updatedParticipant = { ...participant.value, stats: updatedStats }
  participant.value = updatedParticipant
}

// watch
watchEffect(() => {
  homeTeamDirty.value = dirtyState.value.homeTeamDirty
  awayTeamDirty.value = dirtyState.value.awayTeamDirty
})
</script>

<template>
  <div class="grid grid-cols-1 gap-4 px-2">
    <div class="grid grid-cols-1">
      <TeamStatisticsItem
        v-for="stat in statItems"
        :key="stat.config.sportStat.id"
        :config="stat.config"
        :homeStat="stat.homeStat"
        :homeDirty="stat.homeDirty"
        :awayStat="stat.awayStat"
        :awayDirty="stat.awayDirty"
        @update:homeStat="updateStat($event, 'home')"
        @update:awayStat="updateStat($event, 'away')"
      />
    </div>
    <Accordion
      v-if="statAccordionItems.length > 0"
      v-model="accordion"
      class="-mx-2"
      type="single"
      collapsible
    >
      <AccordionItem value="more">
        <AccordionHeader>
          <AccordionTrigger class="px-4">
            Show {{ accordion ? 'less' : 'more' }}
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          <div class="bg-neutral-light-9/25 grid grid-cols-1 rounded-b-md p-2">
            <TeamStatisticsItem
              v-for="stat in statAccordionItems"
              :key="stat.config.sportStat.id"
              :config="stat.config"
              :homeStat="stat.homeStat"
              :homeDirty="stat.homeDirty"
              :awayStat="stat.awayStat"
              :awayDirty="stat.awayDirty"
              @update:homeStat="updateStat($event, 'home')"
              @update:awayStat="updateStat($event, 'away')"
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
</template>
